import type { RoastedBulkGroupItem } from '../../types/inventory';

export interface RoastedBulkFifoPreviewRow {
  inventoryItemId: string;
  workOrderNumber: string;
  quantityKg: number;
  unitCost: number;
  cost: number;
  /** What the lot holds before this draw, so the UI can show how much survives. */
  lotRemainingBefore: number;
}

/**
 * Mirrors the server's FIFO draw so the user sees which lots a write-off will
 * touch before confirming it.
 *
 * Two details keep it honest:
 *  - The server orders by stock_levels.created_at, while the items list arrives
 *    ordered by work order number. Sorting on stockCreatedAt is what makes the
 *    preview name the same lots the server will.
 *  - Cost comes from unitCostPerKg, which the server derives from the lot's
 *    ORIGINAL quantity. unitPrice is the lot's TOTAL COGS and is not a rate;
 *    dividing it here by the remaining quantity is what made the group average
 *    climb with every consumption. The local division is kept only as a
 *    fallback for a server that predates unitCostPerKg.
 *
 * This is advisory only. The response from the adjust endpoint is what actually
 * happened, and the success message reads from that.
 */
export const previewRoastedBulkFifoDraw = (
  items: RoastedBulkGroupItem[],
  quantityKg: number,
): RoastedBulkFifoPreviewRow[] => {
  if (!Number.isFinite(quantityKg) || quantityKg <= 0) return [];

  const ordered = [...items]
    .filter(item => item.remainingQuantity > 0)
    .sort((a, b) => {
      const aTime = a.stockCreatedAt ? Date.parse(a.stockCreatedAt) : Number.NaN;
      const bTime = b.stockCreatedAt ? Date.parse(b.stockCreatedAt) : Number.NaN;
      if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
      // Lots with no timestamp sort last rather than jumping the queue.
      if (Number.isNaN(aTime)) return 1;
      if (Number.isNaN(bTime)) return -1;
      return aTime - bTime;
    });

  const rows: RoastedBulkFifoPreviewRow[] = [];
  let remaining = quantityKg;

  for (const item of ordered) {
    if (remaining <= 1e-9) break;
    const draw = Math.min(item.remainingQuantity, remaining);
    if (draw <= 0) continue;

    let unitCost = item.unitCostPerKg ?? 0;
    if (!unitCost) {
      const denominator = item.outputWeight > 0 ? item.outputWeight : item.remainingQuantity;
      unitCost = denominator > 0 ? (item.unitPrice ?? 0) / denominator : 0;
    }

    rows.push({
      inventoryItemId: item.inventoryItemId,
      workOrderNumber: item.workOrderNumber,
      quantityKg: draw,
      unitCost,
      cost: draw * unitCost,
      lotRemainingBefore: item.remainingQuantity,
    });
    remaining -= draw;
  }

  return rows;
};
